/**
 * IndexedDB-based candle cache for fast chart rendering
 * Stores candle data per symbol/timeframe for instant loading
 */

import { extractCandleDate, mergeCandles } from './candleMerging';

const DB_NAME = 'DWLFCandleCache';
const DB_VERSION = 1;
const STORE_NAME = 'candles';

class CandleCache {
  constructor() {
    this.db = null;
    this.initPromise = this.init();
  }

  async init() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store with compound key [symbol, timeframe]
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          // Index for quick symbol lookups
          store.createIndex('symbol', 'symbol', { unique: false });
        }
      };
    });
  }

  /**
   * Generate cache key for symbol + timeframe
   */
  _getCacheKey(symbol, timeframe) {
    return `${symbol}:${timeframe}`;
  }

  /**
   * Store candles for a symbol/timeframe
   */
  async storeCandles(symbol, timeframe, candles, meta = {}) {
    await this.initPromise;
    
    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const cacheData = {
      id: this._getCacheKey(symbol, timeframe),
      symbol,
      timeframe,
      candles,
      meta,
      timestamp: Date.now(),
      lastCandleDate: candles.length > 0 ? extractCandleDate(candles[candles.length - 1]) : null
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => resolve(cacheData);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Retrieve cached candles for symbol/timeframe
   */
  async getCandles(symbol, timeframe) {
    await this.initPromise;
    
    const transaction = this.db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.get(this._getCacheKey(symbol, timeframe));
      request.onsuccess = () => {
        const result = request.result;
        resolve(result || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update existing candles with new incremental data
   */
  async updateCandles(symbol, timeframe, newCandles) {
    const cached = await this.getCandles(symbol, timeframe);
    if (!cached) {
      // No existing data, store as-is
      return this.storeCandles(symbol, timeframe, newCandles);
    }

    // Merge new candles with existing
    const mergedCandles = mergeCandles(cached.candles, newCandles);
    
    return this.storeCandles(symbol, timeframe, mergedCandles, cached.meta);
  }

  /**
   * Get the last known candle date for incremental fetching
   */
  async getLastCandleDate(symbol, timeframe) {
    const cached = await this.getCandles(symbol, timeframe);
    return cached?.lastCandleDate || null;
  }

  /**
   * Clear cache for a specific symbol/timeframe
   */
  async clearCandles(symbol, timeframe) {
    await this.initPromise;
    
    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.delete(this._getCacheKey(symbol, timeframe));
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear entire cache
   */
  async clearAll() {
    await this.initPromise;
    
    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Check if cached data is stale (older than maxAge milliseconds)
   */
  isCacheStale(cachedData, maxAgeMs = 5 * 60 * 1000) { // 5 minutes default
    if (!cachedData) return true;
    return (Date.now() - cachedData.timestamp) > maxAgeMs;
  }
}

// Export singleton instance
export const candleCache = new CandleCache();

// Export class for testing
export { CandleCache };